import { isNotDefined } from '@sgme/fp';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { Fragment } from 'react/jsx-runtime';

import { getPremiumSuffix } from '@/App/tiles/Option/utils/premiumUtils.ts';
import { formatStrikeForDisplay } from '@/App/tiles/Option/utils/strikeUtils.ts';
import { useFormatPremium } from '@/App/tiles/Option/utils/useFormatPremium.ts';
import { FormattedFXDate } from '@/components/ui/FormattedFXDate.tsx';
import type { OptionExecutionLegData } from '@/store/growl/state/option.ts';
import type { OptionCurrency } from '@/store/option/selectors/optionSelectors.ts';
import type { OptionSide, OptionWay, PremiumType } from '@/store/option/state.ts';
import type { BidAsk } from '@/types/common.ts';

type Props = {
  legs: OptionExecutionLegData[];
  way: OptionWay;
  premiumType: PremiumType;
  optionCurrency: OptionCurrency;
};

export function LegDetails({ legs, way, premiumType, optionCurrency }: Props) {
  return (
    <div className="bg-primary bg-opacity-05 px-2 mt-1">
      <table className="table table-sm table-borderless w-100">
        <thead>
          <tr className="p-4px">
            <th scope="col" className="text-start p-8px">
              <FormattedMessage id="growl.option.notional" />
            </th>
            <th scope="col" className="text-center p-8px">
              <FormattedMessage id="growl.option.premium" />
            </th>
            <th scope="col" className="text-end p-8px">
              <FormattedMessage id="growl.option.expiry" />
            </th>
          </tr>
        </thead>
        <tbody>
          {legs.map(({ type, side, currency, strike, legIndex, notional, premium, expiryDate, expiryDateTenor }) => {
            const legSide = getSide(way, side ?? 'Buy');
            return (
              <Fragment key={legIndex}>
                <tr>
                  <td colSpan={3} className="text-start text-secondary border-top" data-e2e="growl-option-leg-summary">
                    <FormattedMessage
                      id="growl.option.leg.summary"
                      values={{
                        side: legSide,
                        type,
                        currency: optionCurrency.currency1,
                        strike: formatStrikeForDisplay(strike),
                        legIndex: legIndex + 1,
                      }}
                    />
                  </td>
                </tr>
                <tr>
                  <th scope="row" className="p-8px" data-e2e="growl-option-leg-notional">
                    <FormattedNumber value={notional} /> {currency}
                  </th>
                  <td className="text-center p-8px" data-e2e="growl-option-leg-premium">
                    <Premium
                      premium={premium}
                      legSide={legSide}
                      optionCurrency={optionCurrency}
                      premiumType={premiumType}
                    />
                  </td>
                  <td className="text-end p-8px" data-e2e="growl-option-leg-expiry">
                    <FormattedFXDate value={expiryDate} dateStyle="medium" /> ({expiryDateTenor})
                  </td>
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function getSide(execWay: OptionWay, legSide: OptionSide): OptionSide {
  const side: OptionSide = execWay === 'Bid' ? 'Sell' : 'Buy';
  return legSide === 'Sell' ? invertSide(side) : side;
}

function invertSide(side: OptionSide): OptionSide {
  return side === 'Buy' ? 'Sell' : 'Buy';
}

function Premium({
  premium,
  premiumType,
  optionCurrency: { currency1, currency2, currencyPair },
  legSide,
}: {
  premium?: BidAsk<number>;
  premiumType: PremiumType;
  optionCurrency: OptionCurrency;
  legSide: OptionSide;
}) {
  const formatPremium = useFormatPremium(currencyPair);
  if (isNotDefined(premium)) {
    return null;
  }

  const premiumCategory = premiumType.premiumCategory;
  const premiumCurrency = premiumType.premiumCurrency === 1 ? currency1 : currency2;
  const premiumSuffix = getPremiumSuffix(premiumCategory, premiumCurrency);
  const premiumAmount = legSide === 'Sell' ? premium.bid : premium.ask;
  return (
    <>
      {formatPremium(premiumAmount, premiumCategory)}
      {premiumSuffix}
    </>
  );
}
